import type { BlueprintUnderlay } from '@shared/models/design/blueprint-underlay.model';
import { Coordinate } from '@shared/models/geography.model';
import { Point2D } from '@shared/models/geometry.model';
import {
  convertMetricOffsetToCoordinate,
  getDistance,
} from '../geometry/coordinates.helpers';
import { projectPointOntoSegmentClamped } from '../geometry/geometry.helpers';
import { isBlueprintUnderlaySiblingOf } from './blueprint-underlay-building.helpers';

/** Default real-world width when placing a new underlay (meters). */
export const DEFAULT_BLUEPRINT_WIDTH_METERS = 60;

/** Default underlay opacity for tracing after PDF import. */
export const DEFAULT_BLUEPRINT_OPACITY = 0.5;

const DEG_TO_RAD = Math.PI / 180;

/** ISO A-series names shown next to drawing scale in the Properties panel. */
export type BlueprintIsoPaperName = 'A0' | 'A1' | 'A2' | 'A3' | 'A4';

/** ISO A options for the paper-size chip (portrait short/long edges in mm). */
export const BLUEPRINT_ISO_PAPER_NAMES: readonly BlueprintIsoPaperName[] = [
  'A0',
  'A1',
  'A2',
  'A3',
  'A4',
] as const;

/** ISO A-series short/long edges in mm (portrait orientation). */
const ISO_A_PAPER_MM: ReadonlyArray<{
  name: BlueprintIsoPaperName;
  short: number;
  long: number;
}> = [
  { name: 'A0', short: 841, long: 1189 },
  { name: 'A1', short: 594, long: 841 },
  { name: 'A2', short: 420, long: 594 },
  { name: 'A3', short: 297, long: 420 },
  { name: 'A4', short: 210, long: 297 },
];

type IsoPaperSize = (typeof ISO_A_PAPER_MM)[number];

/** Throw when the ISO table is missing a name the chip can select. */
const requireIsoPaper = (name: BlueprintIsoPaperName): IsoPaperSize => {
  const paper = ISO_A_PAPER_MM.find((entry) => entry.name === name);
  if (!paper) throw new Error(`Missing ISO paper size ${name}`);
  return paper;
};

const isoPaperByName: Record<BlueprintIsoPaperName, IsoPaperSize> = {
  A0: requireIsoPaper('A0'),
  A1: requireIsoPaper('A1'),
  A2: requireIsoPaper('A2'),
  A3: requireIsoPaper('A3'),
  A4: requireIsoPaper('A4'),
};

/** Allow small media-box rounding when matching ISO sizes. */
const PAPER_SIZE_MATCH_TOLERANCE_MM = 2;

/**
 * Pick the closest ISO A paper size for a width/height aspect ratio.
 * Used when PDF media-box size was not persisted so `1:N` can still be shown.
 */
export const inferBlueprintPaperSizeMm = (
  width: number,
  height: number,
): { paperWidthMm: number; paperHeightMm: number } => {
  const aspect = width / height;
  let best = { paperWidthMm: 841, paperHeightMm: 594 };
  let bestDiff = Number.POSITIVE_INFINITY;
  for (const { short, long } of ISO_A_PAPER_MM) {
    for (const [paperWidthMm, paperHeightMm] of [
      [short, long],
      [long, short],
    ] as const) {
      const diff = Math.abs(paperWidthMm / paperHeightMm - aspect);
      if (diff >= bestDiff) continue;
      bestDiff = diff;
      best = { paperWidthMm, paperHeightMm };
    }
  }
  return best;
};

/**
 * Match width/height in mm to an ISO A name (orientation-agnostic).
 */
export const matchBlueprintIsoPaperName = (
  widthMm: number,
  heightMm: number,
): BlueprintIsoPaperName | undefined => {
  if (!(widthMm > 0) || !(heightMm > 0)) return undefined;
  const short = Math.min(widthMm, heightMm);
  const long = Math.max(widthMm, heightMm);
  for (const paper of ISO_A_PAPER_MM) {
    if (
      Math.abs(paper.short - short) <= PAPER_SIZE_MATCH_TOLERANCE_MM &&
      Math.abs(paper.long - long) <= PAPER_SIZE_MATCH_TOLERANCE_MM
    ) {
      return paper.name;
    }
  }
  return undefined;
};

type BlueprintPaperSizeSource = Pick<
  BlueprintUnderlay,
  | 'paperWidthMm'
  | 'paperHeightMm'
  | 'pixelWidth'
  | 'pixelHeight'
  | 'widthMeters'
  | 'heightMeters'
>;

/**
 * Resolve paper size in mm from stored media box, or infer ISO size from aspect.
 */
export const getEffectivePaperSizeMm = (
  underlay: BlueprintPaperSizeSource,
): { widthMm: number; heightMm: number } | undefined => {
  if (
    underlay.paperWidthMm &&
    underlay.paperWidthMm > 0 &&
    underlay.paperHeightMm &&
    underlay.paperHeightMm > 0
  ) {
    return {
      widthMm: underlay.paperWidthMm,
      heightMm: underlay.paperHeightMm,
    };
  }
  const width = underlay.pixelWidth ?? underlay.widthMeters;
  const height = underlay.pixelHeight ?? underlay.heightMeters;
  if (!(width > 0) || !(height > 0)) return undefined;
  const inferred = inferBlueprintPaperSizeMm(width, height);
  return {
    widthMm: inferred.paperWidthMm,
    heightMm: inferred.paperHeightMm,
  };
};

/**
 * Paper-size label for scale context: `A1`, or `500 × 700 mm` when not ISO A.
 */
export const formatBlueprintPaperSizeLabel = (
  underlay: BlueprintPaperSizeSource,
): string | undefined => {
  const size = getEffectivePaperSizeMm(underlay);
  if (!size) return undefined;
  const iso = matchBlueprintIsoPaperName(size.widthMm, size.heightMm);
  if (iso) return iso;
  return `${Math.round(size.widthMm)} × ${Math.round(size.heightMm)} mm`;
};

/**
 * Rotate a local-meter point clockwise around the origin (map view from above).
 * Frame: +x east, +y south — so 90° maps east → south.
 */
export const rotatePoint2DClockwise = (
  point: Point2D,
  degrees: number,
): Point2D => {
  if (degrees === 0) return point;
  const rad = degrees * DEG_TO_RAD;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const [x, y] = point;
  return [x * cos - y * sin, x * sin + y * cos];
};

/**
 * Local-meter corners of a {@link BlueprintUnderlay} in Mapbox image order:
 * top-left (NW), top-right (NE), bottom-right (SE), bottom-left (SW).
 */
export const getBlueprintCornerOffsets = (
  underlay: Pick<
    BlueprintUnderlay,
    'center' | 'widthMeters' | 'heightMeters' | 'rotationDeg'
  >,
): readonly [Point2D, Point2D, Point2D, Point2D] => {
  const halfW = underlay.widthMeters / 2;
  const halfH = underlay.heightMeters / 2;
  const [cx, cy] = underlay.center;
  const local: Point2D[] = [
    [-halfW, -halfH],
    [halfW, -halfH],
    [halfW, halfH],
    [-halfW, halfH],
  ];
  return local.map((p) => {
    const rotated = rotatePoint2DClockwise(p, underlay.rotationDeg);
    return [rotated[0] + cx, rotated[1] + cy] as Point2D;
  }) as [Point2D, Point2D, Point2D, Point2D];
};

/**
 * Convert underlay corners to WGS84 `[lng, lat]` pairs for a Mapbox image source.
 */
export const getBlueprintImageCoordinates = (
  origin: Coordinate,
  underlay: Pick<
    BlueprintUnderlay,
    'center' | 'widthMeters' | 'heightMeters' | 'rotationDeg'
  >,
): [[number, number], [number, number], [number, number], [number, number]] => {
  const corners = getBlueprintCornerOffsets(underlay);
  return corners.map((offset) => {
    const { lng, lat } = convertMetricOffsetToCoordinate(origin, offset);
    return [lng, lat] as [number, number];
  }) as [
    [number, number],
    [number, number],
    [number, number],
    [number, number],
  ];
};

type UnderlayFrame = Pick<
  BlueprintUnderlay,
  'center' | 'widthMeters' | 'heightMeters' | 'rotationDeg'
>;

/** Page UV: −0.5…+0.5 from page center (same frame as ink snap). */
export type BlueprintPageUV = { u: number; v: number };

/**
 * Convert a local-meter point into blueprint page UV (center-origin fractions).
 */
export const metricPointToBlueprintPageUV = (
  underlay: UnderlayFrame,
  point: Point2D,
): BlueprintPageUV => {
  const [cx, cy] = underlay.center;
  const local: Point2D = [point[0] - cx, point[1] - cy];
  const unrotated = rotatePoint2DClockwise(local, -underlay.rotationDeg);
  return {
    u: underlay.widthMeters > 0 ? unrotated[0] / underlay.widthMeters : 0,
    v: underlay.heightMeters > 0 ? unrotated[1] / underlay.heightMeters : 0,
  };
};

/**
 * Convert blueprint page UV back to local meters.
 */
export const blueprintPageUVToMetric = (
  underlay: UnderlayFrame,
  page: BlueprintPageUV,
): Point2D => {
  const local: Point2D = [
    page.u * underlay.widthMeters,
    page.v * underlay.heightMeters,
  ];
  const rotated = rotatePoint2DClockwise(local, underlay.rotationDeg);
  return [rotated[0] + underlay.center[0], rotated[1] + underlay.center[1]];
};

/**
 * Copy map pose (center + rotation) from {@link source} onto {@link underlay}.
 * Keeps new pages aligned with an existing sheet when paper size/scale match.
 */
export const alignBlueprintUnderlayPose = (
  underlay: BlueprintUnderlay,
  source: Pick<BlueprintUnderlay, 'center' | 'rotationDeg'>,
): BlueprintUnderlay => ({
  ...underlay,
  center: [source.center[0], source.center[1]],
  rotationDeg: source.rotationDeg,
});

/**
 * Paper width used for `1:N` math — stored media box, or inferred ISO size.
 */
export const getEffectivePaperWidthMm = (
  underlay: BlueprintPaperSizeSource,
): number | undefined => getEffectivePaperSizeMm(underlay)?.widthMm;

/**
 * Persist inferred paper size when missing so later scale edits stay consistent.
 */
export const ensureBlueprintPaperSize = (
  underlay: BlueprintUnderlay,
): BlueprintUnderlay => {
  if (underlay.paperWidthMm && underlay.paperWidthMm > 0) return underlay;
  const width = underlay.pixelWidth ?? underlay.widthMeters;
  const height = underlay.pixelHeight ?? underlay.heightMeters;
  if (!(width > 0) || !(height > 0)) return underlay;
  const paper = inferBlueprintPaperSizeMm(width, height);
  return { ...underlay, ...paper };
};

/**
 * Approximate drawing scale N for a `1:N` label from PDF paper width and placed
 * real-world width. After calibration this updates automatically because
 * {@link BlueprintUnderlay.widthMeters} changes while `paperWidthMm` stays fixed:
 * `N = (widthMeters × 1000) / paperWidthMm` (e.g. 100 m on a 500 mm sheet → `1:200`).
 * Returns undefined when paper size is unknown and cannot be inferred.
 */
export const getBlueprintDrawingScaleRatio = (
  underlay: Pick<
    BlueprintUnderlay,
    | 'widthMeters'
    | 'heightMeters'
    | 'paperWidthMm'
    | 'paperHeightMm'
    | 'pixelWidth'
    | 'pixelHeight'
  >,
): number | undefined => {
  const paperMm = getEffectivePaperWidthMm(underlay);
  if (!paperMm || !(paperMm > 0) || !(underlay.widthMeters > 0)) {
    return undefined;
  }
  return (underlay.widthMeters * 1000) / paperMm;
};

/**
 * Adjust a measured known length so the same UV span implies {@link targetRatio}
 * instead of {@link currentRatio}. Scale edit is authoritative; length follows.
 */
export const adjustKnownLengthForDrawingScaleRatio = (
  knownLengthMeters: number,
  currentRatio: number,
  targetRatio: number,
): number | undefined => {
  if (!(knownLengthMeters > 0) || !Number.isFinite(knownLengthMeters)) {
    return undefined;
  }
  if (!(currentRatio > 0) || !Number.isFinite(currentRatio)) return undefined;
  if (!(targetRatio > 0) || !Number.isFinite(targetRatio)) return undefined;
  if (currentRatio === targetRatio) return knownLengthMeters;
  const next = knownLengthMeters * (targetRatio / currentRatio);
  if (!(next > 0) || !Number.isFinite(next)) return undefined;
  return next;
};

/**
 * Resize the underlay so its drawing scale becomes `1:{@link ratio}`.
 * Persists inferred paper size when the underlay had none.
 * When a primary calibration exists, updates its known length so Length stays
 * consistent with the new scale.
 */
export const setBlueprintDrawingScaleRatio = (
  underlay: BlueprintUnderlay,
  ratio: number,
): BlueprintUnderlay => {
  if (!(ratio > 0) || !Number.isFinite(ratio)) return underlay;
  const withPaper = ensureBlueprintPaperSize(underlay);
  const paperMm = withPaper.paperWidthMm;
  if (!paperMm || !(paperMm > 0)) return underlay;
  const currentRatio = getBlueprintDrawingScaleRatio(withPaper);
  // Ground width (m) = paper width (mm) × N / 1000 for a 1:N drawing.
  const resized = setBlueprintWidthMeters(withPaper, (ratio * paperMm) / 1000);
  const primary = withPaper.calibration?.primary;
  if (!primary || currentRatio === undefined) return resized;
  const nextMeters = adjustKnownLengthForDrawingScaleRatio(
    primary.knownLengthMeters,
    currentRatio,
    ratio,
  );
  if (nextMeters === undefined || nextMeters === primary.knownLengthMeters) {
    return resized;
  }
  return {
    ...resized,
    calibration: {
      ...withPaper.calibration,
      primary: { ...primary, knownLengthMeters: nextMeters },
    },
  };
};

/**
 * Set the underlay paper to an ISO A size, keeping the current `1:N` drawing
 * scale (map size changes with the new paper). Preserves landscape/portrait
 * from the current effective paper size when possible.
 */
export const setBlueprintIsoPaperSize = (
  underlay: BlueprintUnderlay,
  name: BlueprintIsoPaperName,
): BlueprintUnderlay => {
  const paper = isoPaperByName[name];
  if (!paper) return underlay;
  const current = getEffectivePaperSizeMm(underlay);
  const landscape = current
    ? current.widthMm >= current.heightMm
    : underlay.widthMeters >= underlay.heightMeters;
  const paperWidthMm = landscape ? paper.long : paper.short;
  const paperHeightMm = landscape ? paper.short : paper.long;
  if (
    underlay.paperWidthMm === paperWidthMm &&
    underlay.paperHeightMm === paperHeightMm
  ) {
    return underlay;
  }
  const withPaper: BlueprintUnderlay = {
    ...underlay,
    paperWidthMm,
    paperHeightMm,
  };
  const ratio = getBlueprintDrawingScaleRatio(underlay);
  if (ratio === undefined) return withPaper;
  return setBlueprintDrawingScaleRatio(withPaper, ratio);
};

export type BlueprintScaleApplyScope = 'active' | 'all';

/**
 * Apply a drawing scale to one underlay, or to every underlay of the active
 * page's building.
 * When {@link options.source} is set, stamps {@link BlueprintUnderlay.drawingScaleSource}
 * on every underlay that was resized.
 */
export const applyBlueprintDrawingScaleRatio = (
  underlays: readonly BlueprintUnderlay[],
  ratio: number,
  options: {
    scope: BlueprintScaleApplyScope;
    activeIndex: number;
    /** Building in view; scopes "all" when the active page has none. */
    buildingId?: string;
    source?: BlueprintUnderlay['drawingScaleSource'];
  },
): BlueprintUnderlay[] =>
  underlays.map((underlay, index) => {
    if (options.scope === 'active' && index !== options.activeIndex) {
      return underlay;
    }
    const active = underlays[options.activeIndex];
    // "All pages" means the pages of the active page's building.
    if (
      active &&
      !isBlueprintUnderlaySiblingOf(underlay, active, options.buildingId)
    ) {
      return underlay;
    }
    const scaled = setBlueprintDrawingScaleRatio(underlay, ratio);
    if (!options.source) return scaled;
    return { ...scaled, drawingScaleSource: options.source };
  });

/**
 * True when a scale patch kept size, paper, scale source, and calibrated length.
 */
const isBlueprintScalePatchUnchanged = (
  before: BlueprintUnderlay,
  after: BlueprintUnderlay,
): boolean =>
  after.widthMeters === before.widthMeters &&
  after.heightMeters === before.heightMeters &&
  after.paperWidthMm === before.paperWidthMm &&
  after.paperHeightMm === before.paperHeightMm &&
  after.drawingScaleSource === before.drawingScaleSource &&
  after.calibration?.primary?.knownLengthMeters ===
    before.calibration?.primary?.knownLengthMeters;

/**
 * Apply a `1:N` drawing scale to one page on the live list, or to every page.
 * Returns {@link live} when the page is gone, or when the edit changes
 * neither size, paper, calibrated length, nor scale source.
 */
export const patchBlueprintDrawingScale = (
  live: readonly BlueprintUnderlay[],
  options: {
    pageId: string;
    ratio: number;
    scope: BlueprintScaleApplyScope;
    /** Building in view; scopes "all" when the page has none. */
    buildingId?: string;
    /** True when the user typed in the field, so the ratio is stamped `manual`. */
    userTypedScale: boolean;
  },
): readonly BlueprintUnderlay[] => {
  const pageIndex = live.findIndex((page) => page.id === options.pageId);
  const page = live[pageIndex];
  if (!page) return live;
  if (!(options.ratio > 0) || !Number.isFinite(options.ratio)) return live;

  const currentRatio = getBlueprintDrawingScaleRatio(page);
  const ratioChanged =
    currentRatio === undefined ||
    Math.round(currentRatio) !== Math.round(options.ratio);
  const source =
    options.userTypedScale ||
    page.drawingScaleSource === 'manual' ||
    ratioChanged
      ? ('manual' as const)
      : undefined;
  const next = applyBlueprintDrawingScaleRatio(live, options.ratio, {
    scope: options.scope,
    activeIndex: pageIndex,
    buildingId: options.buildingId,
    ...(source ? { source } : {}),
  });
  const pageNext = next[pageIndex];
  if (!pageNext) return live;

  if (options.scope === 'active') {
    if (isBlueprintScalePatchUnchanged(page, pageNext)) return live;
    return next;
  }
  const everyPageUnchanged = next.every((entry, index) => {
    const before = live[index];
    return (
      before !== undefined && isBlueprintScalePatchUnchanged(before, entry)
    );
  });
  if (everyPageUnchanged) return live;
  return next;
};

/**
 * Set one live page to an ISO A size while keeping `1:N`.
 * Returns {@link live} when the page is missing or the paper size is unchanged.
 */
export const patchBlueprintIsoPaperSize = (
  live: readonly BlueprintUnderlay[],
  pageId: string,
  name: BlueprintIsoPaperName,
): readonly BlueprintUnderlay[] => {
  const index = live.findIndex((page) => page.id === pageId);
  const page = live[index];
  if (!page) return live;
  const next = setBlueprintIsoPaperSize(page, name);
  if (next === page) return live;
  return live.map((entry, entryIndex) => (entryIndex === index ? next : entry));
};

/**
 * Human-readable scale for sanity checks after place/calibrate/resize.
 * Prefers `1:N` when paper size is known or can be inferred; otherwise meters.
 */
export const formatBlueprintScaleLabel = (
  underlay: Pick<
    BlueprintUnderlay,
    | 'widthMeters'
    | 'heightMeters'
    | 'paperWidthMm'
    | 'paperHeightMm'
    | 'pixelWidth'
    | 'pixelHeight'
  >,
): string => {
  const ratio = getBlueprintDrawingScaleRatio(underlay);
  if (ratio !== undefined && Number.isFinite(ratio) && ratio > 0) {
    return `1:${Math.round(ratio)}`;
  }
  return `${underlay.widthMeters.toFixed(1)} × ${underlay.heightMeters.toFixed(1)} m`;
};

export const snapPointToBlueprintCorners = (
  underlay: Pick<
    BlueprintUnderlay,
    'center' | 'widthMeters' | 'heightMeters' | 'rotationDeg'
  >,
  point: Point2D,
  maxDistanceMeters: number,
): { point: Point2D; snapped: boolean } => {
  const corners = getBlueprintCornerOffsets(underlay);
  let best = point;
  let bestDist = maxDistanceMeters;
  for (const corner of corners) {
    const dist = getDistance(point, corner);
    if (dist < bestDist) {
      bestDist = dist;
      best = corner;
    }
  }
  if (bestDist < maxDistanceMeters) return { point: best, snapped: true };
  return { point, snapped: false };
};

/**
 * Snap a local-meter point to the nearest underlay corner or edge segment.
 * Corners win when within range (outer corners of the plan); otherwise edges.
 * Returns the original point when farther than {@link maxDistanceMeters}.
 */
export const snapPointToBlueprintFrame = (
  underlay: Pick<
    BlueprintUnderlay,
    'center' | 'widthMeters' | 'heightMeters' | 'rotationDeg'
  >,
  point: Point2D,
  maxDistanceMeters: number,
): Point2D => {
  const corners = getBlueprintCornerOffsets(underlay);

  let bestCorner = point;
  let bestCornerDist = maxDistanceMeters;
  for (const corner of corners) {
    const dist = getDistance(point, corner);
    if (dist < bestCornerDist) {
      bestCornerDist = dist;
      bestCorner = corner;
    }
  }
  if (bestCornerDist < maxDistanceMeters) return bestCorner;

  let bestEdge = point;
  let bestEdgeDist = maxDistanceMeters;
  for (let i = 0; i < 4; i++) {
    const candidate = projectPointOntoSegmentClamped(
      point,
      corners[i]!,
      corners[(i + 1) % 4]!,
    );
    const dist = getDistance(point, candidate);
    if (dist < bestEdgeDist) {
      bestEdgeDist = dist;
      bestEdge = candidate;
    }
  }
  return bestEdgeDist < maxDistanceMeters ? bestEdge : point;
};

/**
 * Translate underlay center by a local-meter offset (e.g. after design normalization).
 */
export const translateBlueprintUnderlay = (
  underlay: BlueprintUnderlay,
  offset: Point2D,
): BlueprintUnderlay => ({
  ...underlay,
  center: [underlay.center[0] + offset[0], underlay.center[1] + offset[1]],
});

/**
 * Move the underlay so its center sits at `center`.
 */
export const moveBlueprintUnderlay = (
  underlay: BlueprintUnderlay,
  center: Point2D,
): BlueprintUnderlay => ({
  ...underlay,
  center,
});

/**
 * Uniformly scale width/height around the current center.
 */
export const scaleBlueprintUnderlay = (
  underlay: BlueprintUnderlay,
  factor: number,
): BlueprintUnderlay => {
  if (!(factor > 0) || !Number.isFinite(factor)) return underlay;
  return {
    ...underlay,
    widthMeters: underlay.widthMeters * factor,
    heightMeters: underlay.heightMeters * factor,
  };
};

/**
 * Scale around a fixed world-space corner (keeps that corner pinned).
 * `fixedCornerIndex` uses the same 0–3 order as {@link getBlueprintCornerOffsets}.
 */
export const scaleBlueprintUnderlayFromCorner = (
  underlay: BlueprintUnderlay,
  factor: number,
  fixedCornerIndex: number,
): BlueprintUnderlay => {
  if (!(factor > 0) || !Number.isFinite(factor)) return underlay;
  const corners = getBlueprintCornerOffsets(underlay);
  const cornerIndex = (((fixedCornerIndex % 4) + 4) % 4) as 0 | 1 | 2 | 3;
  const fixed = corners[cornerIndex];
  const scaled = scaleBlueprintUnderlay(underlay, factor);
  const nextCorners = getBlueprintCornerOffsets(scaled);
  const nextFixed = nextCorners[cornerIndex];
  const dx = fixed[0] - nextFixed[0];
  const dy = fixed[1] - nextFixed[1];
  return {
    ...scaled,
    center: [scaled.center[0] + dx, scaled.center[1] + dy],
  };
};

/**
 * Set absolute size while preserving aspect ratio from the current underlay.
 */
export const setBlueprintWidthMeters = (
  underlay: BlueprintUnderlay,
  widthMeters: number,
): BlueprintUnderlay => {
  if (!(widthMeters > 0) || !Number.isFinite(widthMeters)) return underlay;
  // Malformed persisted data can carry a zero width; dividing by it would make
  // every downstream metric non-finite.
  if (!(underlay.widthMeters > 0)) return underlay;
  const aspect = underlay.heightMeters / underlay.widthMeters;
  return {
    ...underlay,
    widthMeters,
    heightMeters: widthMeters * aspect,
  };
};

/**
 * Scale the underlay so the distance between two local-meter points on the plan
 * equals `targetMeters`. Points are in the same frame as shape points.
 */
export const calibrateBlueprintByKnownLength = (
  underlay: BlueprintUnderlay,
  pointA: Point2D,
  pointB: Point2D,
  targetMeters: number,
): BlueprintUnderlay => {
  if (!(targetMeters > 0) || !Number.isFinite(targetMeters)) return underlay;
  const current = getDistance(pointA, pointB);
  if (!(current > 0)) return underlay;
  return scaleBlueprintUnderlay(underlay, targetMeters / current);
};

/**
 * Clamp opacity into [0, 1].
 */
export const clampBlueprintOpacity = (opacity: number): number =>
  Math.min(1, Math.max(0, opacity));
